import {
  Analysis,
  AnalysisCalendar,
  CashFlow,
  CashFlowCalendar,
  CashFlowCollection,
  CashFlowNature,
  CashFlowsByNature,
  CashFlowsByNatureWithoutCollection,
  categoriesExcludedFromCashFlowTable,
  GroupedCashFlows,
  transactionCategoryOrder,
  TransactionExpenseCategory,
  typesExcludedFromCashFlowTable,
  GroupedCashFlow,
  TransactionType,
  LabelStrategy,
} from '@algoan/dashboard/model';
import {
  cashFlowIsOfOneOfThoseCategories,
  cashFlowIsOfOneOfThoseTypes,
  sumTwoCashFlows,
  analysisCalendarMonthToDate,
  setToMidOfMonth,
  monitoring,
} from '@algoan/dashboard/utils';
import { transactionCategoryToCashFlowNature } from '@algoan/dashboard/converters';
import { getTotalAmountOfRejectedPaymentsForAMonth, sumTotalForCashFlow } from '@algoan/dashboard/calculators';

interface CashFlowWithIndex extends CashFlow {
  index: number;
}
interface SummedCashFlowWithIndex extends CashFlowWithIndex {
  isSummed: true;
}

export const sortCashFlowsWithinCashFlowNature = (
  cashFlowsByNature: CashFlowsByNatureWithoutCollection,
): CashFlowsByNatureWithoutCollection => {
  const cashFlows = cashFlowsByNature.cashFlows.slice().sort((cashFlow1, cashFlow2) => {
    return cashFlow1.category === cashFlow2.category
      ? sumTotalForCashFlow(cashFlow2) - sumTotalForCashFlow(cashFlow1)
      : transactionCategoryOrder[cashFlow1.category] - transactionCategoryOrder[cashFlow2.category];
  });

  return { ...cashFlowsByNature, cashFlows };
};

export const groupCashFlows = (cashFlows: GroupedCashFlow[]): Array<GroupedCashFlow | CashFlowCollection> =>
  cashFlows.reduce<Array<GroupedCashFlow | CashFlowCollection>>((newCashFlows, cashFlow) => {
    const isAlreadyInserted = newCashFlows.some((cashFlow2) => cashFlow2.category === cashFlow.category);
    if (isAlreadyInserted) {
      return newCashFlows;
    }

    const sameCategoryCashFlows = cashFlows.filter((cashFlow2) => cashFlow2.category === cashFlow.category);

    if (sameCategoryCashFlows.length > 1) {
      const collection: CashFlowCollection = {
        category: cashFlow.category,
        cashFlows: sameCategoryCashFlows,
      };
      newCashFlows.push(collection);

      return newCashFlows;
    }

    newCashFlows.push(cashFlow);

    return newCashFlows;
  }, []);

export const groupCashFlowsWithinCashFlowNature = (
  cashFlowsByNature: CashFlowsByNatureWithoutCollection,
): CashFlowsByNature => {
  const cashFlows = groupCashFlows(cashFlowsByNature.cashFlows);

  return { ...cashFlowsByNature, cashFlows };
};

const doesExistInAdenQualityCalendar = (cashFlowMonth: string, analysisCalendar?: AnalysisCalendar[]): boolean => {
  if (analysisCalendar === undefined) return false;

  const calendarMonths = analysisCalendar.map((calendar) => calendar.month);
  return calendarMonths.includes(cashFlowMonth);
};

export const sumCashFlowsIf = (
  cashFlows: CashFlowWithIndex[],
  condition: (cashFlow: CashFlowWithIndex) => boolean,
): Array<CashFlowWithIndex | SummedCashFlowWithIndex> => {
  const hasCashFlowValidatingCondition = cashFlows.some(condition);

  if (hasCashFlowValidatingCondition) {
    return cashFlows.reduce<Array<CashFlowWithIndex | SummedCashFlowWithIndex>>((accumulator, cashFlow) => {
      const conditionTrue = condition(cashFlow);
      const firstConditionTrueIndex = accumulator.findIndex(condition);

      if (!conditionTrue) {
        accumulator.push(cashFlow);

        return accumulator;
      }

      if (firstConditionTrueIndex === -1) {
        accumulator.push({ ...cashFlow, isSummed: true });

        return accumulator;
      }

      accumulator[firstConditionTrueIndex] = {
        ...sumTwoCashFlows(accumulator[firstConditionTrueIndex], cashFlow),
        index: firstConditionTrueIndex,
        isSummed: true,
      };

      return accumulator;
    }, []);
  }

  return cashFlows;
};

export const formatGroupedCashFlows = (
  analysis: Analysis,
  hasCreditCardAccount: boolean,
): GroupedCashFlows | undefined => {
  if (analysis.creditInsights === undefined || analysis.metadata === undefined) {
    return undefined;
  }

  const calendar = (analysis.metadata?.creditInsights?.calendar ?? analysis.metadata?.calendar ?? []).map((column) => {
    return { ...column, date: setToMidOfMonth(analysisCalendarMonthToDate(column.month)) };
  });
  const cashFlowNature: Record<CashFlowNature, CashFlowsByNatureWithoutCollection> = {
    incomes: {
      total: 0,
      cashFlows: [],
    },
    resources: {
      total: 0,
      cashFlows: [],
    },
    expenses: {
      total: 0,
      cashFlows: [],
    },
    fixedCharges: {
      total: 0,
      cashFlows: [],
    },
    loans: {
      total: 0,
      cashFlows: [],
    },
    unknown: {
      total: 0,
      cashFlows: [],
    },
  };

  const hasDifferedPayments = analysis.creditInsights.cashFlows.some(
    (cashFlow) => cashFlow.category === TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT,
  );

  let cashFlows = analysis.creditInsights.cashFlows.map<CashFlowWithIndex | SummedCashFlowWithIndex>(
    (cashFlow, index) => ({ ...cashFlow, index }),
  );

  if (hasCreditCardAccount) {
    cashFlows = cashFlows.filter((cashFlow) => cashFlow.category !== TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT);
  } else if (!hasCreditCardAccount && hasDifferedPayments) {
    cashFlows = combineCardPaymentsAndDeferredTotalPayments(cashFlows);
  }

  cashFlows = sumCashFlowsIf(
    cashFlows,
    (cashFlow) =>
      cashFlow.category === TransactionExpenseCategory.OTHER_EXPENSE && cashFlow.type === TransactionType.CARD,
  );
  cashFlows = sumCashFlowsIf(
    cashFlows,
    (cashFlow) =>
      cashFlow.category === TransactionExpenseCategory.OTHER_EXPENSE && cashFlow.type === TransactionType.CHECK,
  );
  cashFlows = sumCashFlowsIf(
    cashFlows,
    (cashFlow) =>
      cashFlow.category === TransactionExpenseCategory.OTHER_EXPENSE &&
      cashFlow.type === TransactionType.CASH_WITHDRAWAL,
  );

  cashFlows.forEach((cashFlow) => {
    if (
      cashFlowIsOfOneOfThoseCategories(cashFlow, categoriesExcludedFromCashFlowTable) ||
      cashFlowIsOfOneOfThoseTypes(cashFlow, typesExcludedFromCashFlowTable)
    ) {
      return;
    }

    let nature: CashFlowNature = transactionCategoryToCashFlowNature[cashFlow.category] ?? 'unknown';

    const total = cashFlow.totalAmount;

    const computeLabelStrategy = (): LabelStrategy => {
      if (
        cashFlow.category === TransactionExpenseCategory.OTHER_EXPENSE &&
        cashFlowIsOfOneOfThoseTypes(cashFlow, [
          TransactionType.CARD,
          TransactionType.CASH_WITHDRAWAL,
          TransactionType.CHECK,
        ])
      ) {
        if (
          cashFlowIsOfOneOfThoseTypes(cashFlow, [
            TransactionType.INCOMING_TRANSFER,
            TransactionType.OUTGOING_TRANSFER,
          ]) &&
          cashFlow.labelRoot !== undefined
        ) {
          return 'labelRoot';
        }
        return 'categoryAndType';
      } else {
        return 'default';
      }
    };

    const regularCashFlow: GroupedCashFlow = {
      id: cashFlow.id,
      index: cashFlow.index,
      category: cashFlow.category,
      label: cashFlow.label,
      calendar: cashFlow.calendar.reduce<CashFlowCalendar>((calendar, currentMonth) => {
        if (
          doesExistInAdenQualityCalendar(
            currentMonth.month,
            analysis.metadata?.creditInsights?.calendar ?? analysis.metadata?.calendar,
          )
        ) {
          calendar[currentMonth.month] = {
            amount: Math.abs(currentMonth.amount),
            totalRejectedAmount: getTotalAmountOfRejectedPaymentsForAMonth(cashFlow.transactions, currentMonth.month),
          };
        }
        return calendar;
      }, {}),
      total,
      transactions: cashFlow.transactions,
      type: cashFlow.type,
      labelRoot: cashFlow.labelRoot,
      labelStrategy: computeLabelStrategy(),
      isSummed: 'isSummed' in cashFlow && cashFlow.isSummed,
    };

    if (nature === 'unknown') {
      monitoring.logError({
        fingerprint: 'unknown_cash_flow_category',
        message: 'Unknown category for cash flow',
        context: { cashFlow: cashFlow, level: 'error' },
      });

      return;
    }

    const doesNotHaveAnyMonthlyValue = Object.keys(regularCashFlow.calendar).length === 0;

    if (doesNotHaveAnyMonthlyValue) {
      return;
    }

    if (cashFlow.category === TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT) {
      nature = cashFlow.totalAmount < 0 ? 'expenses' : 'incomes';
    }

    cashFlowNature[nature].total += total;
    cashFlowNature[nature].cashFlows.push(regularCashFlow);
  });

  return {
    calendar,
    fixedCharges: groupCashFlowsWithinCashFlowNature(sortCashFlowsWithinCashFlowNature(cashFlowNature.fixedCharges)),
    expenses: groupCashFlowsWithinCashFlowNature(sortCashFlowsWithinCashFlowNature(cashFlowNature.expenses)),
    resources: groupCashFlowsWithinCashFlowNature(sortCashFlowsWithinCashFlowNature(cashFlowNature.resources)),
    incomes: groupCashFlowsWithinCashFlowNature(sortCashFlowsWithinCashFlowNature(cashFlowNature.incomes)),
    loans: sortCashFlowsWithinCashFlowNature(cashFlowNature.loans),
  };
};

const combineCardPaymentsAndDeferredTotalPayments = (cashFlows: CashFlowWithIndex[]): CashFlowWithIndex[] => {
  const cardPaymentCashFlowIndex = cashFlows?.findIndex(
    (cashFlow) => cashFlow.category === TransactionExpenseCategory.CARD_PAYMENT,
  );

  if (cardPaymentCashFlowIndex !== undefined && cardPaymentCashFlowIndex > -1) {
    cashFlows
      .filter(
        (cashFlow) =>
          cashFlow.category === TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT && cashFlow.totalAmount < 0,
      )
      .forEach((cashFlow) => {
        cashFlows[cardPaymentCashFlowIndex] = {
          ...sumTwoCashFlows(cashFlows[cardPaymentCashFlowIndex], cashFlow),
          index: cardPaymentCashFlowIndex,
        };
      });
  }

  return cashFlows.filter((cashFlow) => cashFlow.category !== TransactionExpenseCategory.DEFERRED_TOTAL_PAYMENT);
};
